import { lazy, Suspense, useEffect } from 'react';
import { Header } from './components/Layout/Header';
import { AppSidebar } from './components/Layout/Sidebar';
import { BottomNav } from './components/Layout/BottomNav';
import { Footer } from './components/Layout/Footer';
import { OfflineBanner } from './components/Layout/OfflineBanner';
import { YearCalendar } from './components/Calendar';
import { CarryOverWarning } from './components/Dashboard';
import { useNavigation } from './context/NavigationContext';
import { useAuth } from './auth';
import { SidebarProvider } from '@/components/ui/sidebar';
import { PageSuspenseFallback } from './components/common/PageSuspenseFallback';

const CompanySettings = lazy(() => import('./components/Settings/CompanySettings').then(m => ({ default: m.CompanySettings })));
const FAQ = lazy(() => import('./components/FAQ/FAQ').then(m => ({ default: m.FAQ })));
const HRDashboard = lazy(() => import('./components/HR/HRDashboard').then(m => ({ default: m.HRDashboard })));
const ManagerDashboard = lazy(() => import('./components/Manager/ManagerDashboard').then(m => ({ default: m.ManagerDashboard })));
const HelpPage = lazy(() => import('./components/Help/HelpPage').then(m => ({ default: m.HelpPage })));
const OrgChart = lazy(() => import('./components/OrgChart/OrgChart').then(m => ({ default: m.OrgChart })));
const HRSettings = lazy(() => import('./components/HR/HRSettings').then(m => ({ default: m.HRSettings })));
const EmployeeOnePager = lazy(() => import('./components/Employee').then(m => ({ default: m.EmployeeOnePager })));
const AbsenceApprovalDashboard = lazy(() => import('./components/Approval/AbsenceApprovalDashboard').then(m => ({ default: m.AbsenceApprovalDashboard })));

// Whitelist gespiegelt aus EMPLOYEE_NAV_GROUPS in src/components/Layout/Sidebar.tsx
// + EMPLOYEE_MAIN_ITEMS/EMPLOYEE_MORE_TABS in BottomNav. Bei Aenderungen dort
// auch hier nachziehen.
const EMPLOYEE_ALLOWED_TABS: ReadonlySet<string> = new Set(['employee', 'help', 'faq']);

export default function App() {
  const { activeTab, setActiveTab } = useNavigation();
  const { isHR, isManager } = useAuth();
  const isEmployee = !isHR && !isManager;

  // Employees duerfen nur die Tabs aus der Whitelist oeffnen → alles andere
  // (calendar/settings/hr/hr-settings/manager/orgchart/approvals) zurueck zu 'employee'.
  // Defense-in-Depth: Backend-Routen sind ohnehin durch requireHR/requireManager
  // geschuetzt, aber so wird auch ein manueller Hash-Wechsel (#hr) abgefangen.
  useEffect(() => {
    if (isEmployee && !EMPLOYEE_ALLOWED_TABS.has(activeTab)) {
      setActiveTab('employee');
    }
  }, [isEmployee, activeTab, setActiveTab]);

  const renderContent = () => {
    switch (activeTab) {
      case 'calendar':
        return <YearCalendar />;
      case 'settings':
        return (
          <Suspense fallback={<PageSuspenseFallback title="Einstellungen" />}>
            <CompanySettings />
          </Suspense>
        );
      case 'faq':
        return (
          <Suspense fallback={<PageSuspenseFallback title="FAQ" />}>
            <FAQ />
          </Suspense>
        );
      case 'help':
        return (
          <Suspense fallback={<PageSuspenseFallback title="Hilfe" />}>
            <HelpPage />
          </Suspense>
        );
      case 'manager':
        return (
          <Suspense fallback={<PageSuspenseFallback title="Team-Dashboard" />}>
            <ManagerDashboard />
          </Suspense>
        );
      case 'hr':
        return (
          <Suspense fallback={<PageSuspenseFallback title="HR-Dashboard" />}>
            <HRDashboard />
          </Suspense>
        );
      case 'orgchart':
        return (
          <Suspense fallback={<PageSuspenseFallback title="Organigramm" />}>
            <OrgChart />
          </Suspense>
        );
      case 'hr-settings':
        return (
          <Suspense fallback={<PageSuspenseFallback title="HR-Einstellungen" />}>
            <HRSettings />
          </Suspense>
        );
      case 'employee':
        return (
          <Suspense fallback={<PageSuspenseFallback title="Meine Übersicht" />}>
            <EmployeeOnePager />
          </Suspense>
        );
      case 'approvals':
        return (
          <Suspense fallback={<PageSuspenseFallback title="Genehmigungen" />}>
            <AbsenceApprovalDashboard />
          </Suspense>
        );
      default:
        return <YearCalendar />;
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />

      <div className="flex flex-col flex-1 min-w-0 bg-background">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2
                     focus:px-4 focus:py-2 focus:bg-gray-900 dark:focus:bg-gray-100 focus:text-white dark:focus:text-gray-900 focus:rounded-lg
                     focus:text-sm focus:font-medium focus:outline-none"
        >
          Zum Inhalt springen
        </a>

        <Header />

        <main id="main-content" className="flex-1 flex flex-col pb-20 lg:pb-0">
          <div className="flex-1 p-4 lg:p-6">
            <CarryOverWarning />
            {renderContent()}
          </div>
          <Footer />
        </main>

        <OfflineBanner />
        <BottomNav />
      </div>
    </SidebarProvider>
  );
}
